/**
 * Authentication hooks using React Query
 * Provides login, logout, user data, and auth status management
 */

import { queryOptions, useMutation, useQuery } from "@tanstack/react-query"
import { useCallback, useState, useEffect } from "react"
import { queryClient } from "../queryClient"
import { getCloudflareUrl } from "~/lib/config"
import {
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  subscribeToTokenChanges,
} from "./storage"
import authApi, { type RegisterCredentials } from "../server/cf/auth"
import { CloudflareApiError } from "../server/cf/client"
import { syncIdentity } from "~/lib/identity.client"

/**
 * /me may fail for transient reasons (dev API restart, Zion blip, 5xx).
 * Only a definitive 401 means the session is dead — matching the contract
 * documented in apps/web-api/src/routes/auth/me.ts.
 */
const isUnauthorizedError = (error: unknown): boolean =>
  error instanceof CloudflareApiError && error.status === 401

// Auth API base URL
const AUTH_API_BASE = `${getCloudflareUrl()}/api/auth`

// Types for API requests/responses
interface LoginRequest {
  username: string
  password: string
}

interface LoginResponse {
  success: boolean
  token: string
  message?: string
}

interface ErrorResponse {
  success: false
  message: string
  error?: string
}

/**
 * Query options for fetching current user data
 */
export const userQueryOptions = queryOptions({
  queryKey: ["user"],
  queryFn: async () => {
    const token = getAuthToken()
    if (!token) {
      throw new Error("No auth token available")
    }

    try {
      const data = await authApi.me()
      return { me: data.user }
    } catch (error) {
      if (isUnauthorizedError(error)) {
        clearAuthToken()
      }
      throw error
    }
  },
  staleTime: 5000,
  gcTime: Infinity, // Keep user data cached indefinitely like mobile app
  retry: (failureCount, error) => {
    // Don't retry on auth errors
    if (isUnauthorizedError(error) || error.message.includes("403")) {
      return false
    }
    return failureCount < 1
  },
})

/**
 * Hook to get current user data.
 * Clears the token only when /me returns 401 (session actually invalid).
 */
export const useUser = () => {
  const token = getAuthToken()

  return useQuery({
    ...userQueryOptions,
    enabled: !!token, // Only run if we have a token
  })
}

/**
 * Member photos for the sign-in screen background. Public (no auth token).
 * The endpoint returns an empty list when unconfigured or on upstream failure;
 * consumers fall back to a static image. Cached for an hour.
 */
export const useSignInPhotos = () => {
  return useQuery({
    queryKey: ["sign-in-photos"],
    queryFn: async () => (await authApi.signInPhotos()).photos,
    staleTime: 60 * 60 * 1000,
    gcTime: Infinity,
    retry: 1,
  })
}

/**
 * Keeps analytics identity (FullStory + Sentry) in sync with the current user.
 * PRIMARY identify path: it reads committed React Query render state, so it
 * fires however the user became known, including rehydration of the persisted
 * cache on reload. Mount once at the app root; syncIdentity() dedups by id.
 */
export const useIdentitySync = (): void => {
  const { data } = useUser()
  const user = data?.me ?? null

  useEffect(() => {
    syncIdentity(user)
  }, [user])
}

/**
 * Hook for user authentication token management
 * Reactively subscribes to token changes from storage
 */
export const useAuthToken = () => {
  const [token, setTokenState] = useState<string | null>(() => getAuthToken())

  useEffect(() => {
    // Sync with current token on mount in case it changed
    const currentToken = getAuthToken()
    if (currentToken !== token) {
      setTokenState(currentToken)
    }

    return subscribeToTokenChanges((newToken) => {
      setTokenState(newToken)
      // Update query cache when token changes
      void queryClient.invalidateQueries({ queryKey: ["user"] })
    })
  }, [])

  const setToken = useCallback((newToken: string | null) => {
    setAuthToken(newToken) // This will emit the change and update our state
  }, [])

  return {
    token,
    setToken,
    clearToken: () => setToken(null),
    isAuthenticated: token !== null,
  }
}

/**
 * Login mutation hook
 * Handles username/password login and token storage
 */
export const useLogin = () => {
  return useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response = await fetch(`${AUTH_API_BASE}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      })

      if (!response.ok) {
        const errorData: ErrorResponse =
          (await response.json()) as ErrorResponse
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const data: LoginResponse = (await response.json()) as LoginResponse

      if (!data.success) {
        throw new Error(data.message || "Login failed")
      }

      return data
    },
    onSuccess: async (data) => {
      const token = data.token

      if (token) {
        setAuthToken(token)

        // Invalidate all queries to refetch with new auth
        await queryClient.invalidateQueries()
      } else {
        throw new Error("Login failed: No token received")
      }
    },
    onError: (_error) => {
      clearAuthToken()
    },
  })
}

/**
 * Register mutation hook
 * Handles email/password registration and token storage
 */
export const useRegister = () => {
  return useMutation({
    mutationFn: async (credentials: RegisterCredentials) => {
      const data = await authApi.register(credentials)

      if (!data.success) {
        throw new Error("Registration failed")
      }

      return data
    },
    onSuccess: async (data) => {
      const token = data.token

      if (token) {
        setAuthToken(token)

        // Invalidate all queries to refetch with new auth
        await queryClient.invalidateQueries()
      } else {
        throw new Error("Registration failed: No token received")
      }
    },
    onError: (_error) => {
      clearAuthToken()
    },
  })
}

/**
 * Logout hook
 * Clears token and all cached data
 */
export const useLogout = () => {
  const logout = useCallback(() => {
    try {
      // Clear token from storage
      clearAuthToken()

      // Clear all query cache
      queryClient.clear()
    } catch (error) {}
  }, [])

  return { logout }
}

/**
 * Combined auth status hook
 * Provides comprehensive auth state information
 */
export const useAuthStatus = () => {
  const { token, isAuthenticated } = useAuthToken()
  const {
    data: userData,
    isLoading: isUserLoading,
    error: userError,
  } = useUser()

  return {
    isAuthenticated,
    isUserLoading,
    user: userData?.me,
    userError,
    token,
  }
}
